import type { Campaign, CampaignGroupType, CampaignType } from '~/types/graphql';
import type { ICampaignGroup } from '~/modules/campaign-group';

import {
  UnpublishedTemplateContent,
  DeletedTemplateContent,
  CombinedTemplateContent,
} from './components';
import { useTemplateStatus, useUnpublishedTemplateActions } from '~/modules/campaign-template';
import { BannerTemplateWrapper } from './BannerTemplateWrapper';
import { useCampaignNavigate } from '~/modules/campaign/hooks';
import { INavigateKey } from '~/types/navigate';

interface BannerUnpublishTemplatesProps {
  campaign: Campaign | ICampaignGroup;
  targetCampaign?: { type?: CampaignType | CampaignGroupType; id: string };
  isBannerInCard?: boolean;
  screen?: INavigateKey;
}

export const BannerUnpublishTemplates = ({
  isBannerInCard,
  campaign,
  targetCampaign,
  screen,
}: BannerUnpublishTemplatesProps) => {
  const { onEditCampaign } = useCampaignNavigate();

  const {
    deletedTemplates,
    unpublishedTemplates,
    hasDeleted,
    hasUnpublished,
    hasUnpublishedAndDeleted,
  } = useTemplateStatus({
    campaign,
  });

  const { openPageToRepublish } = useUnpublishedTemplateActions();

  const isDetailsScreen = screen === INavigateKey.CampaignDetails;

  const navigateToEditCampaign = () => {
    onEditCampaign(targetCampaign ?? campaign);
  };

  if (!hasDeleted && !hasUnpublished) return null;

  const renderContent = () => {
    if (hasUnpublishedAndDeleted) {
      return (
        <CombinedTemplateContent
          unpublishedTemplates={unpublishedTemplates}
          deletedTemplates={deletedTemplates}
          onRepublish={openPageToRepublish}
          onEditCampaign={navigateToEditCampaign}
          isDetailsScreen={isDetailsScreen}
        />
      );
    }

    if (hasUnpublished) {
      return (
        <UnpublishedTemplateContent
          unpublishedTemplates={unpublishedTemplates}
          onRepublish={openPageToRepublish}
          isBannerInCard={isBannerInCard}
        />
      );
    }

    return (
      <DeletedTemplateContent
        deletedTemplates={deletedTemplates}
        onEditCampaign={navigateToEditCampaign}
        isDetailsScreen={isDetailsScreen}
      />
    );
  };

  return (
    <BannerTemplateWrapper
      isBannerInCard={isBannerInCard}
      hasUnpublishedAndDeleted={hasUnpublishedAndDeleted}
      screen={screen}
    >
      {renderContent()}
    </BannerTemplateWrapper>
  );
};
